import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { 
  Globe, 
  Users, 
  TrendingUp, 
  Shield, 
  Handshake, 
  Award, 
  Download,
  CheckCircle,
  ArrowRight,
  Mail,
  Phone
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const Partners = () => {

  const partnerBenefits = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Exclusive Regional License",
      description: "Operate NationCard™ with full exclusivity in your designated region"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Dedicated Support",
      description: "Comprehensive onboarding, technical support, and ongoing sales assistance"
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Co-Marketing Opportunities",
      description: "Joint marketing campaigns and brand alignment initiatives"
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Partner Portal Access",
      description: "Exclusive resources, legal templates, analytics, and business tools"
    }
  ];

  const timelineSteps = [
    {
      step: "01",
      title: "Apply & Qualify",
      description: "Submit your regional application and review partnership criteria."
    },
    {
      step: "02", 
      title: "Onboard & Train",
      description: "Receive platform training, co-branding assets, and legal/licensing documents."
    },
    {
      step: "03",
      title: "Operate & Grow", 
      description: "Launch your local NationCard™ chapter, collect revenue, and expand your community."
    }
  ];

  const currentPartners = [
    {
      country: "United Kingdom",
      partner: "UK Democratic Insights Ltd",
      logo: "🇬🇧",
      metric: "32% survey growth in 8 months",
      quote: "NationCard™ has revolutionized how we understand public opinion in the UK."
    },
    {
      country: "Canada", 
      partner: "Canadian Polling Solutions",
      logo: "🇨🇦",
      metric: "50,000+ active participants",
      quote: "The platform's real-time capabilities have transformed our research methodologies."
    },
    {
      country: "Australia",
      partner: "Aussie Opinion Network", 
      logo: "🇦🇺",
      metric: "25% survey growth in 6 months",
      quote: "Outstanding support and a platform that delivers real insights."
    },
    {
      country: "Germany",
      partner: "Deutsche Meinungsforschung GmbH",
      logo: "🇩🇪", 
      metric: "40,000+ verified pollsters",
      quote: "Exceptional technology platform with comprehensive partner support."
    }
  ];

  const successStories = [
    {
      partner: "UK Democratic Insights Ltd",
      country: "United Kingdom",
      logo: "🇬🇧",
      metric: "32% survey growth in 8 months",
      description: "Leveraged NationCard™'s real-time polling capabilities to become the UK's leading digital opinion platform."
    },
    {
      partner: "Canadian Polling Solutions", 
      country: "Canada",
      logo: "🇨🇦",
      metric: "50,000+ active participants",
      description: "Built a thriving community of verified pollsters across all Canadian provinces using our comprehensive toolkit."
    }
  ];

  const faqs = [
    {
      question: "What are the licensing terms?",
      answer: "Partners receive exclusive regional licensing rights with flexible terms ranging from 3-10 years, renewable upon mutual agreement. Revenue sharing is structured to support sustainable growth for both parties."
    },
    {
      question: "How does revenue sharing work?",
      answer: "Our revenue model is transparent and performance-based, with competitive splits that reward active community building and platform growth. Detailed financial terms are provided during the qualification process."
    },
    {
      question: "What kind of exclusivity do I get?",
      answer: "Partners receive complete territorial exclusivity within their designated region, including digital marketing rights and local business development opportunities."
    },
    {
      question: "What are the minimum commitments?",
      answer: "We require partners to demonstrate commitment to community building and platform growth, with specific milestones outlined in the partnership agreement based on regional market potential."
    },
    {
      question: "What support levels are provided?",
      answer: "Partners receive comprehensive support including technical onboarding, marketing materials, ongoing training, dedicated account management, and 24/7 technical assistance."
    },
    {
      question: "How long does the application process take?",
      answer: "The typical application and approval process takes 4-6 weeks, including due diligence, qualification reviews, and legal documentation preparation."
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-[97px] pb-16 md:pt-[105px] md:pb-24 overflow-hidden bg-gradient-to-br from-nation-blue via-nation-blue-dark to-nation-teal">
        {/* Background overlay */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/hero-13-1920x480.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-nation-blue/90 via-nation-blue-dark/85 to-nation-teal/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center min-h-[400px] lg:min-h-[600px]">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Handshake size={16} className="mr-2" />
                    <span>Partnership Program</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight break-words">
                      Join the{" "}
                      <span className="text-nation-teal break-words">
                        NationCard™ Partner Network
                      </span>
                    </h1>
                    <p className="text-lg sm:text-xl md:text-2xl opacity-90 leading-relaxed">
                      Bring real-time public-opinion intelligence to your region with exclusive licensing, training, and support.
                    </p>
                    <p className="text-base sm:text-lg md:text-xl opacity-80 leading-relaxed text-nation-teal">
                      Exclusive regional licensing with comprehensive support
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 bg-nation-teal hover:bg-nation-teal-dark text-white h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      asChild
                    >
                      <a href="#apply">
                        <span className="hidden sm:inline">Apply to Be a Partner</span>
                        <span className="sm:hidden">Apply Now</span>
                        <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      <Download className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      <span className="hidden sm:inline">Download Partner Overview</span>
                      <span className="sm:hidden">Download PDF</span>
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm opacity-80">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Globe size={14} className="sm:w-4 sm:h-4" />
                      <span>Global Network</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Shield size={14} className="sm:w-4 sm:h-4" />
                      <span>Exclusive Licensing</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Users size={14} className="sm:w-4 sm:h-4" />
                      <span>Dedicated Support</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive World Map Visual */}
              <div className="relative mt-8 lg:mt-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 lg:p-8 border border-white/20">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 items-center justify-center">
                    {currentPartners.map((partner, index) => (
                      <div key={index} className="text-center group hover:scale-105 transition-transform duration-200">
                        <div className="text-2xl sm:text-3xl lg:text-4xl mb-2">{partner.logo}</div>
                        <div className="text-xs lg:text-sm font-medium text-white break-words">{partner.country}</div>
                        <div className="text-xs text-white/70 mt-1 break-words">{partner.metric}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-4 lg:mt-6">
                    <Globe className="h-10 w-10 lg:h-12 lg:w-12 mx-auto text-white/60 animate-pulse" />
                    <p className="text-xs lg:text-sm text-white/70 mt-2">Global Partner Network</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview Timeline */}
      <section className="py-16 bg-muted/50">
        <div className="container-full max-w-screen-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How the Program Works</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our chapter-licensing model provides comprehensive platform access and ongoing partner support 
              to ensure your success in bringing NationCard™ to your region.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {timelineSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-nation-blue text-white text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
                {index < timelineSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full">
                    <ArrowRight className="h-6 w-6 text-nation-blue/40 mx-auto" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-16">
        <div className="container-full max-w-screen-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Partner with NationCard™?</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Join a network of forward-thinking organizations transforming how public opinion is captured and analyzed worldwide.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerBenefits.map((benefit, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow duration-200 border-border/50">
                <CardContent className="p-0">
                  <div className="text-nation-blue mb-4">{benefit.icon}</div>
                  <h3 className="text-lg font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Partner Directory */}
      <section className="py-16 bg-muted/50">
        <div className="container-full max-w-screen-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Global Footprint</h2>
            <p className="text-lg text-muted-foreground">
              Meet our trusted partners bringing NationCard™ to communities worldwide
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {currentPartners.map((partner, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-200 border-border/50">
                <CardContent className="p-0">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{partner.logo}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{partner.partner}</h3>
                      <p className="text-nation-blue font-medium">{partner.country}</p>
                      <p className="text-sm text-muted-foreground mt-2 italic">"{partner.quote}"</p>
                      <Badge variant="secondary" className="mt-3">
                        {partner.metric}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="mt-4 w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Partner
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16">
        <div className="container-full max-w-screen-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Partner Highlights</h2>
            <p className="text-lg text-muted-foreground">
              Real results from our partnership network
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {successStories.map((story, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-shadow duration-200 border-border/50">
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-3xl">{story.logo}</div>
                    <div>
                      <h3 className="font-semibold text-lg">{story.partner}</h3>
                      <p className="text-nation-blue">{story.country}</p>
                    </div>
                  </div>
                  <div className="bg-nation-teal/10 rounded-lg p-4 mb-4">
                    <div className="text-2xl font-bold text-nation-teal">{story.metric}</div>
                  </div>
                  <p className="text-muted-foreground mb-4">{story.description}</p>
                  <Button variant="link" className="p-0 text-nation-blue">
                    Read Full Story <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/50">
        <div className="container-full max-w-screen-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about our partnership program
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-nation-blue">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Apply & Next Steps */}
      <section id="apply" className="py-16">
        <div className="container-full max-w-screen-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Join?</h2>
            <p className="text-lg text-muted-foreground">
              Start your partnership journey with NationCard™ today
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Card className="relative p-6 sm:p-8 lg:p-12 border-border/50 bg-gradient-to-br from-white/80 to-blue-50/80 dark:from-card/80 dark:to-blue-950/80 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute -inset-4 bg-gradient-to-r from-nation-purple/20 via-nation-teal/20 to-nation-blue/20 rounded-2xl blur-xl"></div>
              <CardContent className="p-0 relative z-10">
                <div className="text-center space-y-8">
                  <div className="space-y-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-nation-teal to-nation-blue text-white shadow-lg">
                      <Handshake className="h-10 w-10" />
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
                        Start Your Partnership Application
                      </h3>
                      <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        Ready to bring NationCard™ to your region? Our comprehensive contact form allows you to 
                        attach business plans, company profiles, and other partnership materials to help us 
                        understand your vision.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 py-6 sm:py-8 text-xs sm:text-sm">
                      <div className="flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border border-green-200/50 dark:border-green-800/50">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="text-muted-foreground break-words">File attachments supported</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border border-blue-200/50 dark:border-blue-800/50">
                        <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                        <span className="text-muted-foreground break-words">Direct admin integration</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200/50 dark:border-purple-800/50 sm:col-span-2 lg:col-span-1">
                        <Award className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 flex-shrink-0" />
                        <span className="text-muted-foreground break-words">Partnership category pre-selected</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg" 
                      className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-nation-teal to-nation-blue hover:from-nation-teal/90 hover:to-nation-blue/90 text-white h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      asChild
                    >
                      <a href="/contact?category=partnerships">
                        <span className="hidden sm:inline">Submit Partnership Application</span>
                        <span className="sm:hidden">Submit Application</span>
                        <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 border-border/50 hover:bg-background/80 h-auto backdrop-blur-sm hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      <Download className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      <span className="hidden sm:inline">Download Partner Overview</span>
                      <span className="sm:hidden">Download PDF</span>
                    </Button>
                  </div>
                  
                  <p className="text-xs sm:text-sm text-muted-foreground/80 italic px-4">
                    Need to discuss your application first? Our partnerships team is standing by to help.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-nation-blue">
        <div className="container-full max-w-screen-2xl mx-auto px-4 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 break-words">Questions? Contact Our Partnerships Team</h3>
          <Button variant="secondary" size="lg" asChild>
            <a href="/contact">
              <Phone className="h-5 w-5 mr-2" />
              Get in Touch
            </a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Partners;